<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <page-header
      :title="$t('users')"
      :subtitle="supplierName"
      icon="mdi-account-group"
      icon-color="primary"
      :show-back="true"
      @back="$router.back()"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_worker") }}
        </primary-button>
      </template>
    </page-header>

    <div class="px-6 mt-2">
      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        :loading-text="$t('loading_data')"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar color="primary" size="32" class="mr-2">
              <v-icon color="white" size="18">mdi-account</v-icon>
            </v-avatar>
            <span class="font-weight-medium">{{ item.name }} {{ item.lastname }}</span>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <edit-button class="mr-2" @click="openEditDialog(item)" />
          <remove-button @click="removeUser(item)" />
        </template>
      </v-data-table>
    </div>

    <!-- Add / Edit User Dialog -->
    <standard-dialog
      v-model="dialog"
      :title="editedUser ? $t('edit_user') : $t('add_worker')"
      icon="mdi-account-plus"
      :loading="saving"
      @save="saveUser"
    >
      <v-form ref="form">
        <v-row>
          <v-col cols="12" sm="6">
            <text-field
              v-model="form.name"
              :label="$t('name')"
              :rules="fieldError('name')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <text-field
              v-model="form.lastname"
              :label="$t('last_name')"
              :rules="fieldError('lastname')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <text-field
              v-model="form.email"
              :label="$t('email')"
              type="email"
              :rules="fieldError('email')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <text-field
              v-model="form.phone"
              :label="$t('phone_no')"
              kind="phone"
              :rules="fieldError('phone')"
            />
          </v-col>
          <v-col cols="12" v-if="!editedUser">
            <text-field
              v-model="form.password"
              :label="$t('password')"
              type="password"
              prevent-prefill
              :rules="fieldError('password')"
            />
          </v-col>
        </v-row>
      </v-form>
    </standard-dialog>

    <div v-if="device.isMobile" class="fixed-bottom-button">
      <v-btn color="primary" block @click="openAddDialog">
        <v-icon class="mr-2">mdi-plus-circle</v-icon>
        {{ $t("add_worker") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import PageHeader from "@/components/global/PageHeader.vue";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

export default {
  name: "SupplierUsers",
  mixins: [hasSnackbar],
  components: {
    PageHeader,
    StandardDialog,
  },
  data() {
    return {
      users: [],
      loading: false,
      saving: false,
      dialog: false,
      editedUser: null,
      form: {
        name: "",
        lastname: "",
        email: "",
        phone: "",
        password: "",
      },
      formErrors: {},
    };
  },
  computed: {
    supplierId() {
      return this.$route.params.id;
    },
    supplierName() {
      return this.$route.params.name || `#${this.supplierId}`;
    },
    headers() {
      return [
        { text: "ID", value: "id", width: "70px" },
        { text: this.$t("name"), value: "name" },
        { text: this.$t("email"), value: "email" },
        { text: this.$t("phone_no"), value: "phone" },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "right",
          sortable: false,
          width: "120px",
        },
      ];
    },
  },
  created() {
    this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const { data } = await warehouseService.getSupplierUsers(this.supplierId);
        this.users = Array.isArray(data) ? data : data?.data ?? [];
      } catch {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.editedUser = null;
      this.form = { name: "", lastname: "", email: "", phone: "", password: "" };
      this.formErrors = {};
      this.dialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    openEditDialog(user) {
      this.editedUser = user;
      this.form = {
        name: user.name || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phone: user.phone || "",
        password: "",
      };
      this.formErrors = {};
      this.dialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    async saveUser() {
      this.formErrors = {};
      this.saving = true;
      try {
        if (this.editedUser) {
          await warehouseService.updateSupplierUser(
            this.supplierId,
            this.editedUser.id,
            this.form
          );
          this.showSuccessSnackbar(this.$t("saved_successfully"));
        } else {
          await warehouseService.addSupplierUser(this.supplierId, this.form);
          this.showSuccessSnackbar(this.$t("saved_successfully"));
        }
        this.dialog = false;
        this.fetchUsers();
      } catch (e) {
        if (e.response?.data?.errors) {
          this.formErrors = e.response.data.errors;
          this.$nextTick(() => this.$refs.form?.validate());
        } else {
          this.showErrorSnackbar(this.$t("error"));
        }
      } finally {
        this.saving = false;
      }
    },
    fieldError(field) {
      const err = this.formErrors[field];
      if (!err) return [];
      return [() => (Array.isArray(err) ? err[0] : err)];
    },
    async removeUser(user) {
      try {
        await this.$refs.confirmBox.open(
          this.$t("delete_confirmation"),
          `${this.$t("confirm_delete_user")}: ${this.toLatin(user.name)} ${this.toLatin(user.lastname)}?`,
          { color: "error" }
        );
        await warehouseService.removeSupplierUser(this.supplierId, user.id);
        this.users = this.users.filter((u) => u.id !== user.id);
        this.showSuccessSnackbar(this.$t("deleted_successfully"));
      } catch {
        // cancelled or error
      }
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
